import React, { useMemo } from 'react';
import {
  MenuList,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@one-thd/sui-atomic-components';
import { ArrowForward } from '@one-thd/sui-icons';
import {
  IMWCommonMenuView,
  TConcreteRouteGroup,
} from '../../models/navigation';
import {
  mapMainMenuToCommonView,
  mapSubmenuToCommonView,
} from '../../utils/routes';

export interface IMenuItemsProps {
  menu: TConcreteRouteGroup[];
  currentMenuTitle: string | null;
  isSubmenu: boolean;
  onClick: (title: string | null) => void;
}

export const MenuItems: React.FC<IMenuItemsProps> = ({
  menu,
  isSubmenu = false,
  currentMenuTitle,
  onClick,
}) => {
  const menuItems: IMWCommonMenuView[] = useMemo(() => {
    if (!menu || menu.length === 0) return [];

    return isSubmenu && currentMenuTitle
      ? mapSubmenuToCommonView(menu, currentMenuTitle)
      : mapMainMenuToCommonView(menu);
  }, [isSubmenu, currentMenuTitle, menu]);

  return (
    <MenuList className="sui-list-none sui-m-0 sui-px-0 sui-pb-2 sui-relative">
      {menuItems.map((item) => (
        <div
          key={item.label}
          className="sui-flex-auto sui-overflow-y-auto sui-bg-primary sui-flex sui-border-b-1 sui-border-primary sui-border-solid"
          role="menu"
        >
          {isSubmenu ? (
            <SubmenuItem key={item.label} label={item.label} path={item.path} />
          ) : (
            <StandardMenuItem
              key={item.label}
              label={item.label}
              path={item.path}
              IconComponent={item.icon}
              onClick={onClick}
              links={!!item.links && item.links.length > 0}
            />
          )}
        </div>
      ))}
    </MenuList>
  );
};

interface MenuItemProps {
  label: string;
  path: string;
}

interface StandardMenuItemProps extends MenuItemProps {
  IconComponent: React.ElementType | null;
  links: boolean;
  onClick: (label: string) => void;
}

const StandardMenuItem: React.FC<StandardMenuItemProps> = ({
  label,
  path,
  IconComponent,
  onClick,
  links,
}) => {
  const testId = `${label.split(' ').join('-')}-menu-item`;

  return (
    <MenuItem
      component="a"
      data-testid={testId}
      tabIndex={0}
      onClick={() => {
        if (!links) {
          return;
        }
        onClick(label);
      }}
      href={path}
    >
      <span className="sui-h-12 sui-w-full sui-flex sui-items-center">
        {IconComponent && (
          <ListItemIcon>
            <IconComponent color="primary" />
          </ListItemIcon>
        )}
        <ListItemText primary={label} />
        {links && <ArrowForward size="small" />}
      </span>
    </MenuItem>
  );
};

const SubmenuItem: React.FC<MenuItemProps> = ({ label, path }) => {
  const testId = `${label.split(' ').join('-')}-submenu-item`;

  return (
    <MenuItem component="a" data-testid={testId} href={path}>
      <span className="sui-h-12 sui-w-full sui-flex sui-items-center">
        <span>{label}</span>
      </span>
    </MenuItem>
  );
};
